import * as React from "react";
import Box from "@mui/material/Box";
import { ButtonGroup, IconButton, Table as MUITable } from "@mui/material/";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { visuallyHidden } from "@mui/utils";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { useTheme } from "@mui/material/";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { zohoApi } from "../../zohoApi";

const ellipsisStyle = {
  // display: "inline-block",
  // width: "9rem",
  // overflow: "hidden",
  // whiteSpace: "nowrap",
  // textOverflow: "ellipsis",
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)

function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, headCells } = props;
  const theme = useTheme();
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ position: "sticky", top: 0, zIndex: 1 }}>
      <TableRow
        sx={{
          bgcolor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
        }}
      >
        {headCells.map((headCell) => {
          if (headCell?.id === "buttons") {
            return (
              <TableCell
                sx={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  color: "currentcolor",
                  "& .MuiTableSortLabel-root:hover": {
                    color: "currentcolor",
                  },
                  "& .MuiTableSortLabel-root.Mui-active": {
                    color: "currentcolor",
                    "& .MuiTableSortLabel-icon": { color: "currentcolor" },
                  },
                }}
                key={headCell?.id}
                align="left"
                padding="none"
                sortDirection={orderBy === headCell?.id ? order : false}
              >
                <Box as="span" sx={ellipsisStyle}>
                  {headCell.label}
                </Box>
              </TableCell>
            );
          }
          return (
            <TableCell
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                color: "currentcolor",
                "& .MuiTableSortLabel-root:hover": {
                  color: "currentcolor",
                },
                "& .MuiTableSortLabel-root.Mui-active": {
                  color: "currentcolor",
                  "& .MuiTableSortLabel-icon": { color: "currentcolor" },
                },
              }}
              key={headCell?.id}
              align="left"
              sortDirection={orderBy === headCell?.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === headCell?.id}
                direction={orderBy === headCell?.id ? order : "asc"}
                onClick={createSortHandler(headCell?.id)}
                sx={{
                  "& svg": { color: "currentcolor" },
                }}
              >
                <Box as="span" sx={ellipsisStyle}>
                  {headCell.label}
                </Box>

                {orderBy === headCell?.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export function SignatureTable({
  tablePlaceholderHeight = "100%",
  rows,
  rowstaLoading,
  handleSelectAllClick,
  selected,
  dialogData,
  setDialogData,
  orgId,
  apiKey,
  dataCenterUrl,
  setReload,
  setDataFromDB,
}) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [openConfirmation, setOpenConfirmation] = React.useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows = React.useMemo(() => {
    return stableSort(rows, getComparator(order, orderBy));
  }, [rows, order, orderBy]);

  const headCells = [
    { id: "email", label: "Email" },
    { id: "signature", label: "Signature" },
    { id: "buttons", label: "Edit/Delete" },
  ];

  return (
    <>
      <Box
        sx={{
          p: "0 1px 1px 1px",
          height: tablePlaceholderHeight,
          overflow: "auto",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer sx={{ flexGrow: 1 }}>
            <MUITable aria-labelledby="tableTitle" size="small">
              <EnhancedTableHead
                onSelectAllClick={handleSelectAllClick}
                numSelected={selected?.length}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows?.length}
                headCells={headCells}
              />
              <TableBody>
                {rowstaLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={headCells?.length + 1}
                      sx={{ textAlign: "center", borderBottom: "none" }}
                    >
                      <CircularProgress
                        sx={{ mx: "auto", position: "relative", top: "100px" }}
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows === null || rows?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={headCells?.length + 1}
                      sx={{ textAlign: "center", borderBottom: "none" }}
                    >
                      <Box
                        sx={{
                          mx: "auto",
                          position: "relative",
                          top: "100px",
                        }}
                      >
                        No data
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}

                {visibleRows?.length > 0 ? (
                  <>
                    {visibleRows?.map((el, index) => {
                      const setting_schema = el?.setting_schema;
                      const setting_id = el?.setting_id;
                      const parsedSettingSchema = JSON.parse(
                        setting_schema || "{}"
                      );
                      const row = parsedSettingSchema?.users;

                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row?.id}
                          sx={{ cursor: "pointer" }}
                        >
                          {headCells?.map((headCell) => {
                            if (headCell?.id === "email") {
                              return (
                                <TableCell key={headCell?.id}>
                                  <Box as="span" sx={ellipsisStyle}>
                                    {row?.[headCell?.id]}
                                  </Box>
                                </TableCell>
                              );
                            }

                            if (headCell?.id === "signature") {
                              return (
                                <TableCell key={headCell?.id} padding="none">
                                  <Box
                                    sx={{
                                      width: "100%",
                                      maxHeight: "5rem",
                                      overflowY: "auto",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: row?.[headCell?.id],
                                    }}
                                  />
                                </TableCell>
                              );
                            }

                            if (headCell?.id === "buttons") {
                              return (
                                <TableCell key={headCell?.id} padding="none">
                                  <ButtonGroup
                                    // variant="contained"
                                    aria-label="Button group"
                                  >
                                    <IconButton
                                      aria-label="edit"
                                      onClick={() => {
                                        setDialogData({
                                          object: row,
                                          title: undefined,
                                          content: undefined,
                                          buttonText: "Save",
                                          type: "edit",
                                          setting_id,
                                        });
                                        setOpenConfirmation(true);
                                      }}
                                    >
                                      <RemoveRedEyeOutlinedIcon />
                                    </IconButton>
                                    <IconButton
                                      aria-label="delete"
                                      onClick={() => {
                                        setDialogData({
                                          object: row,
                                          title: "Confirm",
                                          content: "Are you sure?",
                                          buttonText: "Delete",
                                          type: "delete",
                                          setting_id,
                                        });
                                        setOpenConfirmation(true);
                                      }}
                                    >
                                      <DeleteOutlineOutlinedIcon />
                                    </IconButton>
                                  </ButtonGroup>
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </>
                ) : null}
              </TableBody>
            </MUITable>
          </TableContainer>
        </Paper>
      </Box>
      <ConfirmationDialog
        dialogOpen={openConfirmation}
        title={dialogData?.title}
        content={dialogData?.content}
        signature={dialogData?.object?.signature}
        buttonText={dialogData?.buttonText}
        handleDialogClose={() => {
          setOpenConfirmation(false);
        }}
        handleDialogConfirm={async ({ data: newSignature }) => {
          const setting_id = dialogData?.setting_id;
          // if (dialogData?.type === "edit") {
          //   const userObject = dialogData?.object;
          //   const newUserObject = { ...userObject, signature: newSignature };
          //   const dbUpsertResp = await zohoApi.config.upsertConfigToDB({
          //     orgId,
          //     apiKey,
          //     dataCenterUrl,
          //     moduleApiName: "userSignature",
          //     settingId: setting_id || "",
          //     settingSchema: { users: newUserObject },
          //     settingType: undefined,
          //   });

          //   setDataFromDB([]);
          //   setReload((pre) => !pre);
          // }
          if (dialogData?.type === "delete") {
            const dbDeleteResp = await zohoApi.config.deleteConfigFromDB({
              orgId,
              apiKey,
              dataCenterUrl,
              settingId: setting_id || "",
            });

            setDataFromDB([]);
            setReload((pre) => !pre);
          }
          setOpenConfirmation(false);
        }}
      />
    </>
  );
}
